import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowDownToLine } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { RollingNumber } from "../rollingNumber";
import { SpinnerSwap } from "../spinner";

export type SpaceSelectionPillProps = {
  /** Number of selected files; the pill hides itself when this is 0. */
  count: number;
  /** Download the current selection. */
  onDownload: () => void;
};

/**
 * Floating "N files selected" download pill, pinned to the bottom-center of the
 * viewport and floating above the page footer.
 *
 * Presentational and self-positioning ({@link Box} with `position: fixed`); the
 * outer layer is click-through so only the pill itself is interactive. The count
 * uses the odometer {@link RollingNumber} — the same rolling animation as the
 * share-link "download all" button — so the number rolls as the selection grows.
 * Clearing the selection is handled by the breadcrumb select-all checkbox, so
 * the pill carries no separate clear control.
 */
export function SpaceSelectionPill({
  count,
  onDownload,
}: SpaceSelectionPillProps) {
  const { t } = useTranslation();
  // Spinner while the download is prepared (the multi-file ZIP takes a moment).
  const download = useAsyncAction(onDownload);
  const visible = count > 0;

  // Splice the odometer into the translated string at the count's position
  // (which varies by locale) rather than concatenating — mirrors the share-link
  // download button.
  const label =
    count === 1
      ? t("1 file selected")
      : t("{{count}} files selected", { count });
  const token = String(count);
  const at = label.indexOf(token);
  const labelNode: ReactNode =
    at === -1 ? (
      label
    ) : (
      <>
        {label.slice(0, at)}
        <RollingNumber value={count} />
        {label.slice(at + token.length)}
      </>
    );

  return (
    <Fade in={visible} unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 28,
          zIndex: 1200,
          display: "flex",
          justifyContent: "center",
          px: 2,
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", pointerEvents: "auto" }}
        >
          <Button
            onClick={download.run}
            data-download-all
            variant="contained"
            disableElevation
            sx={{
              height: 48,
              px: 3,
              borderRadius: "999px",
              backgroundColor: "var(--primary-color)",
              color: "#ffffff",
              fontSize: 15,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 10px 28px -8px rgba(0,0,0,0.45)",
              "&:hover": {
                backgroundColor: "var(--primary-color)",
                filter: "brightness(0.95)",
              },
            }}
          >
            <SpinnerSwap loading={download.loading} spinnerColor="#ffffff">
              {/* One span so the label is a single flex item — otherwise the
                  Button flex layout trims the space around the rolling number. */}
              <span>{labelNode}</span>
              <FontAwesomeIcon
                icon={faArrowDownToLine as IconProp}
                style={{ fontSize: 15, marginLeft: 8 }}
              />
            </SpinnerSwap>
          </Button>
        </Box>
      </Box>
    </Fade>
  );
}
